import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { EMOTES } from "@zend/content";
import { useT } from "../i18n/index.js";
import { useSession } from "../state/session.js";
import { serverNow } from "../state/serverTime.js";
import { Button } from "../ui/Button.js";
import { chatApi } from "./api.js";
import { useSocial } from "./state.js";
import type { ChatMessage } from "./types.js";
import { EmoteImage } from "../store/cosmeticPresentation.js";
import { GmBadge } from "./GmBadge.js";
import { CardView, MessageBody } from "./MessageParts.js";
import { buildConversation, clockTime, type ConversationItem } from "./conversationModel.js";

const EMPTY: ChatMessage[] = [];
/** Farther than this from the bottom counts as "reading older messages": new ones show a pill instead of scrolling. */
const NEAR_BOTTOM_PX = 120;

/** The open conversation: header with retention hint, grouped bubbles with day separators and the unread divider, new-messages pill, composer (D67). */
export function Conversation() {
  const t = useT();
  const account = useSession((s) => s.account);
  const chat = useSocial((s) => s.chat);
  const channel = useSocial((s) => s.channel);
  const list = useSocial((s) => s.messages[s.channel]);
  const messages = list ?? EMPTY;
  const unreadAtOpen = useSocial((s) => s.unreadAtOpen[s.channel] ?? 0);
  const replyTo = useSocial((s) => s.replyTo);
  const pendingDm = useSocial((s) => s.pendingDm);
  const send = useSocial((s) => s.send);
  const setReplyTo = useSocial((s) => s.setReplyTo);
  const openProfile = useSocial((s) => s.openProfile);
  const closeConversation = useSocial((s) => s.closeConversation);
  const run = useSocial((s) => s.run);
  const [text, setText] = useState("");
  const [emotes, setEmotes] = useState(false);
  const [coolUntil, setCoolUntil] = useState(0);
  const [menu, setMenu] = useState<string | null>(null);
  const [pending, setPending] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const atBottom = useRef(true);
  const lastId = useRef<string | null>(null);

  const items = useMemo(() => buildConversation(messages, account?.id ?? "", unreadAtOpen, serverNow()), [messages, account?.id, unreadAtOpen]);

  useEffect(() => {
    atBottom.current = true;
    setPending(0);
    lastId.current = null;
  }, [channel]);
  useEffect(() => {
    const el = listRef.current;
    const newest = messages[messages.length - 1];
    const own = newest?.accountId === account?.id;
    if (el && (atBottom.current || own || lastId.current === null)) {
      el.scrollTop = el.scrollHeight;
      setPending(0);
    } else if (newest && newest.id !== lastId.current) {
      setPending((n) => n + 1);
    }
    lastId.current = newest?.id ?? null;
  }, [messages, account?.id]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (atBottom.current) setPending(0);
  };
  const jumpDown = () => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    atBottom.current = true;
    setPending(0);
  };
  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (await send(trimmed)) {
      setText("");
      setCoolUntil(Date.now() + (chat?.rateLimitMs ?? 2000));
    }
  };
  const max = chat?.maxLength ?? 120;
  const muted = chat?.mutedUntil && chat.mutedUntil > serverNow();
  const isAdmin = account?.role === "admin";
  const isDm = channel.startsWith("dm:");
  const isAlly = channel.startsWith("ally:");
  const title = channel === "global" ? t("social.chat.global") : channel === "system" ? t("social.chat.system") : isAlly ? (chat?.alliance ? `[${chat.alliance.tag}] ${chat.alliance.name}` : t("nav.alliance")) : (chat?.dms.find((d) => d.channel === channel)?.name ?? pendingDm?.name ?? "…");
  const retention = channel === "global" ? chat?.retentionHours.global ?? 6 : channel === "system" ? chat?.retentionHours.system ?? 24 : isAlly ? chat?.retentionHours.alliance ?? 24 : null;
  const subtitle = isDm ? t("social.chat.dmKept") : t("social.chat.retention", { h: retention ?? 0 });

  const dayText = (label: string) => (label === "today" ? t("social.chat.today") : label === "yesterday" ? t("social.chat.yesterday") : label.split("-").reverse().join("."));

  const bubble = (item: Extract<ConversationItem, { kind: "message" }>) => {
    const m = item.message;
    const own = item.own;
    return (
      <div key={m.id} className={`chat-msg ${own ? "own" : ""} ${m.system ? "system" : ""}`} data-bubble={m.bubble ?? undefined} data-first={item.first} data-last={item.last} data-testid={`msg-${m.id}`}>
        {item.first && !own ? (
          <div className="row chat-author" style={{ gap: 6, alignItems: "baseline" }}>
            {m.system ? (
              <strong style={{ color: m.colour }}>ZEnd</strong>
            ) : (
              <button type="button" className="plain" style={{ color: m.colour, fontWeight: 700 }} onClick={() => void openProfile(m.accountId)} data-testid="msg-author">
                {m.name}
              </button>
            )}
            <GmBadge role={m.role} />
          </div>
        ) : null}
        {m.replyTo ? (
          <div className="chat-quote">
            <strong>{m.replyTo.name}</strong>: {m.replyTo.text}
          </div>
        ) : null}
        <MessageBody m={m} />
        <CardView m={m} />
        <div className="chat-stamp">
          <span className="mono">{clockTime(m.createdAt)}</span>
          {!m.system && !m.deleted ? (
            <button type="button" className="plain dim" onClick={() => setMenu(menu === m.id ? null : m.id)} aria-label="…" data-testid="msg-menu">
              ⋯
            </button>
          ) : null}
        </div>
        {menu === m.id ? (
          <div className="row chat-actions" style={{ gap: 6, flexWrap: "wrap" }}>
            <Button style={{ minHeight: 30 }} onClick={() => { setReplyTo(m); setMenu(null); }} data-testid="msg-reply">
              <FunctionalIcon name="undo" /> {t("social.chat.reply")}
            </Button>
            {!own ? (
              <>
                <Button style={{ minHeight: 30 }} onClick={() => { void run(() => chatApi.report(m.id, "report"), t("social.chat.reported")); setMenu(null); }} data-testid="msg-report">
                  <FunctionalIcon name="flag" /> {t("social.chat.report")}
                </Button>
                <Button style={{ minHeight: 30 }} onClick={() => { void run(() => chatApi.block(m.accountId), t("social.chat.blocked")).then(() => void useSocial.getState().openChannel(channel)); setMenu(null); }} data-testid="msg-block">
                  <FunctionalIcon name="block" /> {t("social.chat.block")}
                </Button>
              </>
            ) : null}
            {isAdmin ? (
              <>
                <Button variant="danger" style={{ minHeight: 30 }} onClick={() => { void run(() => chatApi.adminDelete(m.id)); setMenu(null); }} data-testid="msg-delete">
                  <FunctionalIcon name="close" /> {t("social.chat.delete")}
                </Button>
                <Button variant="danger" style={{ minHeight: 30 }} onClick={() => { void run(() => chatApi.adminMute(m.accountId, 10, "timeout"), t("social.chat.muted")); setMenu(null); }} data-testid="msg-mute">
                  <FunctionalIcon name="clock" /> {t("social.chat.timeout")}
                </Button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="conversation" data-testid="conversation">
      <div className="conv-header">
        <button type="button" className="plain conv-back" onClick={closeConversation} aria-label={t("social.chat.back")} data-testid="chat-back">
          <FunctionalIcon name="arrowLeft" />
        </button>
        <div className="conv-head-text">
          <div className="conv-title" data-testid="conversation-title">{title}</div>
          <div className="conv-sub" data-testid="conversation-subtitle">{subtitle}</div>
        </div>
      </div>
      <div className="chat-list" ref={listRef} onScroll={onScroll} data-testid="chat-list">
        {messages.length >= 200 ? (
          <button type="button" className="plain dim" style={{ alignSelf: "center" }} onClick={() => void useSocial.getState().loadOlder()}>
            {t("social.chat.older")}
          </button>
        ) : null}
        {items.map((item) => {
          if (item.kind === "day") return <div key={item.key} className="chat-day" data-testid="chat-day">{dayText(item.label)}</div>;
          if (item.kind === "unread") return <div key={item.key} className="chat-unread" data-testid="chat-unread-divider">{t("social.chat.unreadDivider", { n: item.n })}</div>;
          return bubble(item);
        })}
        {messages.length === 0 ? <p className="dim" style={{ padding: 12 }}>{t("social.chat.empty")}</p> : null}
        {pending > 0 ? (
          <button type="button" className="chat-new-pill" onClick={jumpDown} data-testid="chat-new-pill">
            <FunctionalIcon name="arrowDown" /> {t("social.chat.new", { n: pending })}
          </button>
        ) : null}
      </div>
      {channel === "system" ? (
        <p className="hint" style={{ padding: "6px 12px" }}>{t("social.chat.systemReadOnly")}</p>
      ) : (
        <div className="chat-composer">
          {replyTo ? (
            <div className="row chat-quote" style={{ gap: 6 }}>
              <span className="grow ellipsis">
                <FunctionalIcon name="undo" /> <strong>{replyTo.name}</strong>: {replyTo.text}
              </span>
              <button type="button" className="plain" onClick={() => setReplyTo(null)}>
                <FunctionalIcon name="close" />
              </button>
            </div>
          ) : null}
          {emotes ? (
            <div className="row emote-picker" data-testid="emote-picker">
              {EMOTES.map((e) => {
                const ok = chat?.emotes.includes(e.id) ?? false;
                return (
                  <button key={e.id} type="button" className="plain" disabled={!ok} title={ok ? `:${e.id}:` : e.pack ? t("store.cos.packRequired") : t("social.chat.emoteLocked", { n: e.unlock })} onClick={() => { setText((x) => `${x} :${e.id}: `.replace(/\s+/g, " ").trimStart()); setEmotes(false); }} style={{ fontSize: 20, opacity: ok ? 1 : 0.3 }}>
                    <EmoteImage id={e.id} />
                  </button>
                );
              })}
            </div>
          ) : null}
          <div className="row" style={{ gap: 6 }}>
            <button type="button" className="map-tool glass" style={{ width: 38, height: 38 }} onClick={() => setEmotes(!emotes)} title={t("social.chat.emotes")} data-testid="emote-toggle">
              <EmoteImage id="robot" size={25} />
            </button>
            <input
              className="field grow"
              value={text}
              maxLength={max}
              placeholder={muted ? t("social.chat.mutedUntil") : t("social.chat.placeholder")}
              disabled={!!muted}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void submit();
                }
              }}
              data-testid="chat-input"
            />
            <Button variant="primary" style={{ minHeight: 38 }} disabled={!text.trim() || !!muted || Date.now() < coolUntil} onClick={() => void submit()} data-testid="chat-send">
              <FunctionalIcon name="send" />
            </Button>
          </div>
          <div className="hint" style={{ textAlign: "right" }}>
            {text.length}/{max}
          </div>
        </div>
      )}
    </div>
  );
}
